import React from "react";
import { NotebookPageContentBlock } from "constants/notebooks/notebook-page-content-blocks";
import { BlockRecord } from "models/view-models/block-record";
import { AdditionalBlockData } from "models/interfaces/additional-block-data/additional-block-data";
import { isCodeReferenceAdditionalData } from "models/interfaces/additional-block-data/additional-code-reference-block-data";
import { TextBlockConfigurationRecord } from "models/view-models/block-configurations/text-block-configuration-record";
import { ImageBlockConfigurationRecord } from "models/view-models/block-configurations/image-block-configuration-record";
import StringUtils from "utilities/string-utils";
import ChecklistItemDisplay from "organisms/checklist-block-editor/checklist-item-display";
import { isChecklistAdditionalData } from "models/interfaces/additional-block-data/additional-checklist-block-data";
import { CollectionUtils } from "./collection-utils";

/**
 * Extracts display content from a block for preview purposes
 * @param block The block to extract content from
 * @param additionalBlockData Additional data needed for certain block types
 * @returns JSX element or string representing the block content
 */

const MAX_CONTENT_LENGTH = 300;
const MAX_CHECKLIST_ITEMS_PREVIEW = 3;
const CHECKLIST_GAP = "1.5rem";

const truncateText = (
    text: string,
    maxLength: number = MAX_CONTENT_LENGTH
): string => {
    return text.length > maxLength
        ? text.slice(0, maxLength).trimEnd() + "…"
        : text;
};

const stripHtmlTags = (html: string): string => {
    return html.replace(/<[^>]*>/g, "");
};

const createFallbackContent = (blockType: string): string => {
    return `${blockType.charAt(0).toUpperCase() + blockType.slice(1)} block`;
};

const renderChecklistItems = (items: any[], maxItems: number) => (
    <div
        style={{
            display: "flex",
            flexDirection: "column",
            gap: CHECKLIST_GAP,
        }}>
        {items.slice(0, maxItems).map((item, index) => (
            <ChecklistItemDisplay
                index={index}
                isEditing={false}
                isNumbered={false}
                item={item}
                key={item.id}
                onUpdateChecklistItemChecked={() => {}}
                printable={false}
                showStatus={false}
                totalItems={items.length}
            />
        ))}
        {items.length > maxItems && (
            <span style={{ fontStyle: "italic" }}>…</span>
        )}
    </div>
);

export const getBlockContent = (
    block: BlockRecord,
    additionalBlockData: AdditionalBlockData = {}
): JSX.Element | string => {
    switch (block.type) {
        case NotebookPageContentBlock.Text:
            try {
                const textConfig = new TextBlockConfigurationRecord(
                    block.configuration
                );
                const cleanText = stripHtmlTags(textConfig.content);
                return truncateText(cleanText);
            } catch {
                return createFallbackContent("text");
            }

        case NotebookPageContentBlock.CodeReference:
            try {
                if (!isCodeReferenceAdditionalData(additionalBlockData)) {
                    return createFallbackContent("code reference");
                }

                const section = additionalBlockData.section;

                if (StringUtils.isEmpty(section?.title)) {
                    return createFallbackContent("code reference");
                }

                return (
                    <>
                        <div style={{ fontWeight: "bold" }}>
                            {section.getTitle()}
                        </div>
                        <p>
                            {StringUtils.truncateRight(
                                section.bodyAsPlainText ?? "",
                                MAX_CONTENT_LENGTH
                            )}
                        </p>
                    </>
                );
            } catch {
                return createFallbackContent("code reference");
            }

        case NotebookPageContentBlock.Checklist:
            try {
                if (!isChecklistAdditionalData(additionalBlockData)) {
                    return createFallbackContent("checklist");
                }

                const items = additionalBlockData.items;

                if (CollectionUtils.isEmpty(items)) {
                    return createFallbackContent("checklist");
                }

                return renderChecklistItems(items, MAX_CHECKLIST_ITEMS_PREVIEW);
            } catch {
                return createFallbackContent("checklist");
            }

        case NotebookPageContentBlock.Image:
            try {
                new ImageBlockConfigurationRecord(block.configuration);
                return createFallbackContent("image");
            } catch {
                return createFallbackContent("image");
            }

        default:
            return "Block Content";
    }
};
